import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Clock, AlertTriangle, Flame, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { CandleChart } from "@/components/CandleChart";
import { PositionCard } from "@/components/PositionCard";
import { Chat } from "@/components/Chat";
import { sfx, isMuted, setMuted, primeAudio } from "@/lib/sounds";

function fmt(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmt2(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function mmss(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// Mirrors backend position_value so we can mark-to-market open positions live
function posValue(pos, price) {
  const entry = pos.entry_price || 1e-6;
  return pos.side === "buy"
    ? pos.amount * (price / entry)
    : Math.max(0, pos.amount * (2 - price / entry));
}

export default function Match() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [active, setActive] = useState(null);
  const [pct, setPct] = useState(25);
  const [error, setError] = useState("");
  const settledRef = useRef(false);
  const [formPct, setFormPct] = useState(0);
  const stepStartRef = useRef(Date.now());
  const [muted, setMutedState] = useState(isMuted());
  const prevStepRef = useRef(null);
  const prevSecRef = useRef(null);
  const cdEndRef = useRef(null);
  const [cdNum, setCdNum] = useState(null);
  const prevCdRef = useRef(null);
  const [flash, setFlash] = useState(false);
  // Generation guard: bumped on every local mutation so a slow in-flight poll
  // cannot overwrite fresh state (fixes close-all occasionally leaving a position).
  const genRef = useRef(0);
  const commitState = (data) => { genRef.current += 1; setState(data); };
  const execMsg = (data) => (data && data.exec_price != null ? ` · ${t("executed_at")} ${fmt2(data.exec_price)}` : "");

  const fetchState = useCallback(async () => {
    const myGen = genRef.current;
    try {
      const { data } = await api.get(`/matches/${id}`);
      if (genRef.current !== myGen) return; // a mutation happened during this poll -> drop stale data
      setState(data);
      setActive((prev) => prev ?? data.instruments[0]?.id);
      if (data.status === "finished" && !settledRef.current) {
        settledRef.current = true;
        (data.me?.won ? sfx.win : sfx.lose)();
        setTimeout(() => navigate(`/results/${id}`), 900);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchState();
    const iv = setInterval(fetchState, 1500);
    return () => clearInterval(iv);
  }, [fetchState]);

  // reset formation clock when a new candle step begins
  const stateRef = useRef(null);
  useEffect(() => { stateRef.current = state; }, [state]);
  const step = state?.step;
  const secondsRemaining = state?.seconds_remaining;
  const pending = state?.pending;
  const waitingPlayers = state?.waiting_players;
  const startsIn = state?.starts_in;

  useEffect(() => {
    if (step == null) return;
    stepStartRef.current = Date.now() - (stateRef.current?.step_fraction || 0) * 5000;
  }, [step]);

  // sound: candle open tick (tenser in last minute)
  useEffect(() => {
    if (step == null) return;
    const s = stateRef.current;
    if (prevStepRef.current != null && step > prevStepRef.current && s?.status === "active") {
      (s.seconds_remaining <= 60 ? sfx.tickTense : sfx.tick)();
    }
    prevStepRef.current = step;
  }, [step]);

  // sound: alarm when entering the last minute
  useEffect(() => {
    if (secondsRemaining == null) return;
    if (prevSecRef.current != null && prevSecRef.current > 60 && secondsRemaining <= 60 && stateRef.current?.status === "active") {
      sfx.alarm();
    }
    prevSecRef.current = secondsRemaining;
  }, [secondsRemaining]);

  // smooth local formation progress (0-100%)
  useEffect(() => {
    const iv = setInterval(() => {
      setFormPct(Math.min(100, ((Date.now() - stepStartRef.current) / 5000) * 100));
    }, 100);
    return () => clearInterval(iv);
  }, []);

  // pre-start 3s countdown: lock the end time once, then tick locally.
  // Only when the match clock is actually anchored (all players present) — not while waiting.
  useEffect(() => {
    if (pending == null) return;
    if (pending && !waitingPlayers && cdEndRef.current == null) {
      cdEndRef.current = Date.now() + (startsIn || 0) * 1000;
    }
    if (!pending || waitingPlayers) cdEndRef.current = null;
  }, [pending, waitingPlayers, startsIn]);

  useEffect(() => {
    const iv = setInterval(() => {
      if (cdEndRef.current == null) { setCdNum(null); return; }
      const rem = Math.ceil((cdEndRef.current - Date.now()) / 1000);
      const val = rem > 0 ? rem : null;
      if (val != null && val !== prevCdRef.current) sfx.tickTense();
      if (val == null && prevCdRef.current != null && prevCdRef.current >= 1) {
        sfx.go();
        setFlash(true);
        setTimeout(() => setFlash(false), 600);
      }
      prevCdRef.current = val;
      setCdNum(val);
    }, 100);
    return () => clearInterval(iv);
  }, []);

  const trade = async (side) => {
    if (!active) return;
    primeAudio();
    try {
      const { data } = await api.post(`/matches/${id}/trade`, { instrument_id: active, side, percent: pct });
      commitState(data);
      (side === "buy" ? sfx.buy : sfx.sell)();
      toast.success(`${side.toUpperCase()} ${pct}% · ${data.instruments.find((i) => i.id === active)?.symbol}${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const closePos = async (index) => {
    try {
      const { data } = await api.post(`/matches/${id}/close`, { index });
      commitState(data);
      sfx.close();
      toast.success(`${t("close")}${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const flipPos = async (index) => {
    try {
      const { data } = await api.post(`/matches/${id}/flip`, { index });
      commitState(data);
      sfx.close();
      toast.success(`${t("flip")}${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const closeAll = async () => {
    try {
      const { data } = await api.post(`/matches/${id}/close-all`);
      commitState(data);
      sfx.close();
      toast.success(`${t("close_all")}${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const closePartial = async (index, percent) => {
    try {
      const { data } = await api.post(`/matches/${id}/close-partial`, { index, percent });
      commitState(data);
      sfx.close();
      toast.success(`-${percent}%${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addSize = async (index, percent) => {
    try {
      const { data } = await api.post(`/matches/${id}/add`, { index, percent });
      commitState(data);
      sfx.trade();
      toast.success(`+${percent}%${execMsg(data)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) { primeAudio(); sfx.tick(); }
  };

  if (error) {
    return <div className="min-h-screen bg-base flex items-center justify-center text-loss font-mono">{error}</div>;
  }
  if (!state) {
    return <div className="min-h-screen bg-base flex items-center justify-center text-white/40 font-mono animate-pulse">loading match…</div>;
  }

  const inst = state.instruments.find((i) => i.id === active) || state.instruments[0];
  const up = inst.change_pct >= 0;
  const priceColor = up ? "#22C55E" : "#EF4444";
  const danger = state.seconds_remaining <= 60;
  const p = state.player;
  // Live mark-to-market: value open positions at the same interpolated forming-candle
  // price the chart shows, so P&L moves smoothly instead of jumping every 5s.
  const liveActive = state.status === "active" && !state.pending;
  const formF = Math.min(1, Math.max(0, formPct / 100));
  const livePrices = {};
  (state.instruments || []).forEach((i) => {
    livePrices[i.id] = liveActive && i.forming
      ? i.forming.o + (i.forming.c - i.forming.o) * formF
      : i.current_price;
  });
  const livePnlFor = (pos) => {
    const price = livePrices[pos.instrument_id];
    return price == null ? (pos.pnl || 0) : posValue(pos, price) - pos.amount;
  };
  const vixHigh = state.current_vix >= 25;
  const showFirstBetWarn = p.first_bet_required && !p.first_bet_deadline_passed;
  const o = inst.ohlc;
  const previewAmount = Math.round((p.cash * pct) / 100);
  const events = inst.events || [];
  const chartMarkers = events.map((e) => ({ step: e.step, text: "•".repeat(e.importance) }));
  const chartTooltips = Object.fromEntries(
    events.map((e) => [e.step, `${t("event_news")} · ${t("importance")}: ${"•".repeat(e.importance)}`])
  );

  return (
    <div className="min-h-screen bg-base text-white p-2 md:p-4 overflow-x-hidden pb-24 lg:pb-4">
      {state.waiting_players && (
        <div data-testid="waiting-overlay" className="fixed inset-0 z-50 bg-base/90 backdrop-blur-sm flex flex-col items-center justify-center">
          <Loader2 className="w-12 h-12 text-signal animate-spin mb-6" />
          <div className="font-mono text-sm uppercase tracking-[0.4em] text-signal">{t("waiting_players_msg")}</div>
          <div className="mt-5 font-mono text-2xl font-black text-white tabular-nums" data-testid="ready-counter">
            {state.ready_count ?? 0}/{state.players_total ?? 0} <span className="text-sm font-normal text-white/50">{t("players_ready")}</span>
          </div>
          <div className="mt-3 font-mono text-white/40 text-xs">{inst?.name}</div>
        </div>
      )}
      {cdNum != null && !state.waiting_players && (
        <div data-testid="countdown-overlay" className="fixed inset-0 z-50 bg-base/90 backdrop-blur-sm flex flex-col items-center justify-center">
          <div className="font-mono text-xs uppercase tracking-[0.4em] text-signal mb-6">{t("get_ready")}</div>
          <div key={cdNum} className="font-heading font-black text-signal timer-danger" style={{ fontSize: "9rem", lineHeight: 1 }}>
            {cdNum}
          </div>
          <div className="mt-8 font-mono text-white/40 text-sm">{inst?.name}</div>
        </div>
      )}

      {/* TOP BAR */}
      <div className="border border-white/10 bg-panel px-4 py-3 flex flex-wrap items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black tracking-tight">SNETCH</span>
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-signal/40 text-signal">
            {state.status === "active" ? t("match_active") : t("match_finished")}
          </span>
        </div>

        <div className="flex items-center gap-3 md:gap-8 flex-wrap justify-end">
          <Stat label={t("cash")} value={fmt(p.cash)} />
          <Stat label={t("equity")} value={fmt2(p.equity)} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("profit")}</div>
            <div className={`font-mono font-bold text-base md:text-lg tabular-nums ${p.profit >= 0 ? "text-profit" : "text-loss"}`} data-testid="player-profit">
              {p.profit >= 0 ? "+" : ""}{fmt2(p.profit)}
            </div>
          </div>
          {(() => {
            const openPnl = (p.positions || []).reduce((s, x) => s + livePnlFor(x), 0);
            return (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("open_pnl")}</div>
                <div className={`font-mono font-bold text-base md:text-lg tabular-nums ${openPnl >= 0 ? "text-profit" : "text-loss"}`} data-testid="open-pnl">
                  {openPnl >= 0 ? "+" : ""}{fmt2(openPnl)}
                </div>
              </div>
            );
          })()}
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${danger ? "text-loss" : "text-white/40"}`} />
            <span
              data-testid="match-timer"
              className={`font-mono font-bold text-xl md:text-2xl tabular-nums ${danger ? "text-loss timer-danger" : "text-white"}`}
            >
              {mmss(state.seconds_remaining)}
            </span>
          </div>
          <button
            data-testid="sound-toggle"
            onClick={toggleSound}
            className="text-white/50 hover:text-signal transition-colors"
            title="Audio"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {showFirstBetWarn && (
        <div className="border border-signal/40 bg-signal/10 text-signal px-4 py-2 mb-3 flex items-center gap-2 font-mono text-sm">
          <AlertTriangle className="w-4 h-4" /> {t("first_bet_warn")}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* CHART + TABS */}
        <div className="lg:col-span-8 min-w-0 border border-white/10 bg-panel flex flex-col">
          {/* instrument tabs */}
          <div className="flex items-stretch border-b border-white/10 overflow-x-auto">
            {state.instruments.map((i) => {
              const activeTab = i.id === active;
              const imp = Math.max(...(i.calendar || []).map((e) => e.importance), 0);
              return (
                <button
                  key={i.id}
                  data-testid={`instrument-tab-${i.symbol}`}
                  onClick={() => setActive(i.id)}
                  className={`px-4 py-3 text-left border-r border-white/10 transition-colors min-w-[130px] ${activeTab ? "bg-panelhover" : "hover:bg-panelhover/50"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-bold text-sm">{i.symbol}</span>
                    <span className="flex gap-0.5">
                      {[1, 2, 3].map((d) => (
                        <span key={d} className={`w-1 h-1 rounded-full ${d <= imp ? "bg-signal" : "bg-white/15"}`} />
                      ))}
                    </span>
                  </div>
                  <div className={`font-mono text-xs tabular-nums mt-1 ${i.change_pct >= 0 ? "text-profit" : "text-loss"}`}>
                    {i.change_pct >= 0 ? "+" : ""}{i.change_pct}%
                  </div>
                </button>
              );
            })}
            {/* VIX indicator */}
            <div className="ml-auto px-4 py-3 flex items-center gap-2 border-l border-white/10">
              <Flame className={`w-4 h-4 ${vixHigh ? "text-vixhi" : "text-white/40"}`} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("vix")}</div>
                <div className={`font-mono font-bold tabular-nums ${vixHigh ? "text-vixhi" : "text-white"}`} data-testid="vix-value">
                  {state.current_vix}
                </div>
              </div>
            </div>
          </div>

          {/* header: name + OHLC + price */}
          <div className="px-4 pt-4 pb-2 flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="font-heading font-bold text-lg">{inst.masked ? t("blind_market") : inst.name}</div>
              <div className="font-mono text-xs text-white/40">
                {inst.masked ? "??? · ???" : `${inst.is_real ? t("real_data") : t("sim_data")} · ${inst.type}`}
              </div>
            </div>
            <div className="font-mono text-xs text-white/50 tabular-nums flex gap-3">
              <span>O <b className="text-white/80">{fmt2(o.o)}</b></span>
              <span>H <b className="text-profit">{fmt2(o.h)}</b></span>
              <span>L <b className="text-loss">{fmt2(o.l)}</b></span>
              <span>C <b className="text-white/80">{fmt2(o.c)}</b></span>
            </div>
            <div className="font-mono font-bold text-2xl tabular-nums" style={{ color: priceColor }} data-testid="instrument-price">
              {fmt2(inst.current_price)}
            </div>
          </div>

          {/* candle chart */}
          <div className="px-2 pb-2 flex-1 relative">
            <CandleChart candles={inst.candles} height={typeof window !== "undefined" && window.innerWidth < 640 ? 300 : 360} markers={chartMarkers} tooltips={chartTooltips} forming={inst.forming} resetKey={inst.id} />
            {flash && (
              <div data-testid="go-flash" className="absolute inset-0 flex items-center justify-center pointer-events-none go-flash">
                <span className="font-heading font-black text-6xl md:text-7xl text-signal drop-shadow-[0_0_25px_rgba(234,179,8,0.8)]">
                  {t("go")}
                </span>
              </div>
            )}
          </div>

          {/* candle progress footer */}
          <div className="border-t border-white/10 px-4 py-2 flex items-center gap-4 font-mono text-xs text-white/40">
            <span data-testid="candle-counter">
              {t("candle")} {Math.min(state.step + 1, state.total_steps)} {t("of")} {state.total_steps}
            </span>
            {inst.forming && state.status === "active" && (
              <span className="flex items-center gap-2 text-signal" data-testid="forming-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-signal animate-pulse" />
                {t("forming")} {Math.round(formPct)}%
              </span>
            )}
            <div className="flex-1 h-1 bg-white/10 overflow-hidden">
              <div className="h-full bg-signal transition-[width] duration-100" style={{ width: `${(state.step / state.total_steps) * 100}%` }} />
            </div>
            <span>{Math.round((state.step / state.total_steps) * 100)}%</span>
          </div>
        </div>

        {/* ACTION PANEL */}
        <div className="lg:col-span-4 min-w-0 border border-white/10 bg-panel p-4 flex flex-col gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("invest_pct")}</div>
              <div className="font-mono text-xs tabular-nums text-signal">≈ {fmt(previewAmount)}</div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              {state.percents.map((pc) => (
                <button
                  key={pc}
                  data-testid={`pct-${pc}`}
                  onClick={() => setPct(pc)}
                  className={`py-3 font-mono font-bold text-sm tabular-nums transition-colors ${pct === pc ? "bg-signal text-black" : "bg-panel hover:bg-panelhover text-white/70"}`}
                >
                  {pc}%
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="buy-button"
              onClick={() => trade("buy")}
              disabled={state.status !== "active" || state.pending || previewAmount < 1}
              className="py-6 bg-profit/90 hover:bg-profit text-black font-heading font-black text-lg rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {t("buy")}
            </button>
            <button
              data-testid="sell-button"
              onClick={() => trade("sell")}
              disabled={state.status !== "active" || state.pending || previewAmount < 1}
              className="py-6 bg-loss/90 hover:bg-loss text-black font-heading font-black text-lg rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {t("sell")}
            </button>
          </div>

          {/* Leaderboard */}
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("leaderboard")}</div>
            <div className="space-y-1">
              <AnimatePresence>
                {state.leaderboard.map((e) => (
                  <motion.div
                    key={e.name}
                    layout
                    transition={{ duration: 0.4 }}
                    data-testid={`leaderboard-${e.rank}`}
                    className={`flex items-center justify-between px-3 py-2 border ${e.is_you ? "border-signal/50 bg-signal/5" : "border-white/10 bg-panelhover/40"}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-white/40 tabular-nums w-4">{e.rank}</span>
                      <span className={`font-mono text-sm ${e.is_you ? "text-signal font-bold" : "text-white/80"}`}>
                        {e.is_you ? t("you") : e.name}
                      </span>
                    </div>
                    <span className={`font-mono text-sm tabular-nums ${e.profit >= 0 ? "text-profit" : "text-loss"}`}>
                      {e.profit >= 0 ? "+" : ""}{fmt(e.profit)}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {state.is_multiplayer && state.room_id && (
            <Chat channel={state.room_id} height={160} />
          )}
        </div>

        {/* POSITIONS TABLE */}
        <div className="lg:col-span-12 border border-white/10 bg-panel p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("positions")}</div>
            {state.status === "active" && p.positions.length > 0 && (
              <button
                data-testid="close-all-btn"
                onClick={closeAll}
                className="px-3 py-1.5 border border-loss/50 text-loss hover:bg-loss/10 rounded-sm text-[11px] font-mono uppercase tracking-wide transition-colors"
              >
                {t("close_all")}
              </button>
            )}
          </div>
          {p.positions.length === 0 ? (
            <div className="text-white/30 font-mono text-sm py-4">{t("none_open")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {p.positions.map((pos, i) => (
                <PositionCard
                  key={`${pos.instrument_id}-${pos.index}`}
                  pos={pos}
                  index={i}
                  active={state.status === "active"}
                  livePnl={livePnlFor(pos)}
                  t={t}
                  onClosePartial={closePartial}
                  onAdd={addSize}
                  onFlip={flipPos}
                  onClose={closePos}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {state.status === "active" && !state.pending && (
        <div
          data-testid="mobile-trade-bar"
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-panel/95 backdrop-blur border-t border-white/10 p-3 flex flex-col gap-2"
        >
          {p.positions.length > 0 && (
            <button
              data-testid="mobile-close-all-btn"
              onClick={closeAll}
              className="w-full py-2.5 border border-loss/50 text-loss active:bg-loss/10 rounded-sm font-mono text-xs uppercase tracking-widest transition-colors"
            >
              {t("close_all")}
            </button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="mobile-buy-button"
              onClick={() => trade("buy")}
              disabled={previewAmount < 1}
              className="py-4 bg-profit/90 active:bg-profit text-black font-heading font-black text-base rounded-sm transition-colors disabled:opacity-30"
            >
              {t("buy")} · {pct}%
            </button>
            <button
              data-testid="mobile-sell-button"
              onClick={() => trade("sell")}
              disabled={previewAmount < 1}
              className="py-4 bg-loss/90 active:bg-loss text-black font-heading font-black text-base rounded-sm transition-colors disabled:opacity-30"
            >
              {t("sell")} · {pct}%
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-mono font-bold text-base md:text-lg tabular-nums">{value}</div>
  </div>
);
